import React, { useState, useEffect } from 'react';
import { Table } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { lotServiceQa } from '../../../utils/pins_services';
import Channels_link from '../crm/modals/Channels_link';
import './PinsLotes.css';

const extractPageParam = (url) => {
  if (!url) return null;
  try { return new URL(url).searchParams.get('page'); } catch { return null; }
};

function PinsLotesQA({ usr_id, permission, token }) {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionToken = token || localStorage.getItem('token');
  const permissionLevel = Number(permission || 0);
  const isReadOnly = permissionLevel !== 3;
  
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [nextPageParam, setNextPageParam] = useState(null);
  const [prevPageParam, setPrevPageParam] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pinQuantity, setPinQuantity] = useState('');
  const [lotNote, setLotNote] = useState('');
  const [creatingLot, setCreatingLot] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('warning');
  const [resellerModalOpen, setResellerModalOpen] = useState(false);
  const [selectedLotKey, setSelectedLotKey] = useState(null);
  const [lotResellerMap, setLotResellerMap] = useState({});
  const [exportInProgress, setExportInProgress] = useState(false);
  const itemsPerPage = 20;

  useEffect(() => {
    if (permissionLevel !== 3) {
      navigate('/pins/estado');
      return;
    }
    if (sessionToken) {
      fetchLots(null);
    } else {
      navigate('/');
    }
  }, [sessionToken, permissionLevel, navigate]);

  useEffect(() => {
    if (!toastMessage) {
      return undefined;
    }
    const timer = setTimeout(() => setToastMessage(''), 6000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const showToast = (message, variant = 'warning') => {
    setToastVariant(variant);
    setToastMessage(message);
  };

  const fetchLots = async (pageParam = null) => {
    setLoading(true);
    try {
      const response = await lotServiceQa.getAll(pageParam, sessionToken);

      if (response === 'INVALID') {
        navigate('/');
        return;
      }

      const applyPagination = (nextUrl, prevUrl, page) => {
        setCurrentPage(page ?? 1);
        setHasNextPage(nextUrl != null);
        setHasPrevPage(prevUrl != null);
        setNextPageParam(extractPageParam(nextUrl));
        setPrevPageParam(extractPageParam(prevUrl));
      };

      if (response && response.data && Array.isArray(response.data)) {
        setLots(response.data);
        applyPagination(response.next_page_url, response.prev_page_url, response.current_page);
        loadLotChannels(response.data);
      } else if (response && response.lots && Array.isArray(response.lots)) {
        setLots(response.lots);
        applyPagination(null, null, 1);
        loadLotChannels(response.lots);
      } else if (Array.isArray(response)) {
        setLots(response);
        applyPagination(null, null, 1);
        loadLotChannels(response);
      } else if (response && typeof response === 'object') {
        const dataKey = Object.keys(response).find(key => Array.isArray(response[key]));
        if (dataKey) {
          setLots(response[dataKey]);
          applyPagination(response.next_page_url, response.prev_page_url, response.current_page);
          loadLotChannels(response[dataKey]);
        } else {
          setLots([]);
          applyPagination(null, null, 1);
        }
      } else {
        setLots([]);
        applyPagination(null, null, 1);
      }
    } catch (error) {
      console.error('Error al cargar lotes:', error);
      setLots([]);
      setHasNextPage(false);
      setHasPrevPage(false);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const handleCreateLot = async () => {
    if (!sessionToken) {
      navigate('/');
      return;
    }
    if (isReadOnly) {
      return;
    }
    if (creatingLot) {
      return;
    }
    const quantity = parseInt(pinQuantity, 10);
    if (!quantity || quantity <= 0 || !lotNote.trim()) {
      return;
    }
    setCreatingLot(true);
    try {
      const response = await lotServiceQa.create(quantity, lotNote.trim(), sessionToken);
      if (response === 'INVALID') {
        navigate('/');
        return;
      }
      if (response?.error === 'PinCreateLotServiceError') {
        setShowCreateModal(false);
        showToast(
          'La generación está tardando más de lo esperado. Revisa el lote más tarde para confirmar que esté completo.',
          'warning'
        );
        return;
      }
      setShowCreateModal(false);
      setPinQuantity('');
      setLotNote('');
      fetchLots(null);
    } catch (error) {
      console.error('Error al crear lote:', error);
      const timeoutMessage = error?.message || error?.response?.data?.message || '';
      if (timeoutMessage.toLowerCase().includes('timed out')) {
        setShowCreateModal(false);
        showToast(
          'La generación está tardando más de lo esperado. Revisa el lote más tarde para confirmar que esté completo.',
          'warning'
        );
      } else {
        showToast('Ocurrió un error al generar el lote. Intenta nuevamente.', 'danger');
      }
    } finally {
      setCreatingLot(false);
    }
  };

  const loadLotChannels = async (lotsData) => {
    if (!sessionToken || !Array.isArray(lotsData)) {
      return;
    }
    const lotValues = lotsData
      .map((lot) => lot.identify || lot.lot_number || lot.id)
      .filter((value) => value !== undefined && value !== null && value !== '');

    if (lotValues.length === 0) {
      return;
    }

    try {
      const response = await lotServiceQa.getLotChannels(lotValues, sessionToken);
      if (response === 'INVALID') {
        navigate('/');
        return;
      }
      if (Array.isArray(response)) {
        const nextMap = {};
        response.forEach((item) => {
          if (item?.lot) {
            nextMap[item.lot] = {
              channelId: item.channel_id,
              channelName: item.channel_name
            };
          }
        });
        setLotResellerMap((prev) => ({
          ...prev,
          ...nextMap
        }));
      }
    } catch (error) {
      console.error('Error al cargar resellers del lote:', error);
    }
  };

  const openResellerModal = (lotKey) => {
    setSelectedLotKey(lotKey);
    setResellerModalOpen(true);
  };

  const handleSetReseller = async (channelId, channelName) => {
    if (!selectedLotKey || !sessionToken) {
      navigate('/');
      return;
    }
    try {
      const response = await lotServiceQa.linkToChannel(selectedLotKey, channelId, sessionToken);
      if (response === 'INVALID') {
        navigate('/');
        return;
      }
      setLotResellerMap((prev) => ({
        ...prev,
        [selectedLotKey]: { channelId, channelName }
      }));
      setResellerModalOpen(false);
    } catch (error) {
      console.error('Error al asociar lote con reseller:', error);
    }
  };

  const handleExportLot = async (lotId) => {
    if (!sessionToken) {
      navigate('/');
      return;
    }
    if (!lotId) {
      return;
    }
    setExportInProgress(true);
    try {
      const response = await lotServiceQa.exportLot(lotId, sessionToken);
      if (response === 'INVALID') {
        navigate('/');
        return;
      }
      if (!response || !response.blob) {
        return;
      }
      const url = window.URL.createObjectURL(response.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.filename || `lot_${lotId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar lote:', error);
    } finally {
      setExportInProgress(false);
    }
  };

  return (
    <div className="pins-lotes-container">
      <h1 className="pins-lotes-title">LOTES DE PINES QA</h1>
      
      <div className="pins-lotes-table-container">
        <Table hover className="pins-lotes-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Numero de Lote</th>
              <th>Observación</th>
              <th>Creado</th>
              <th>Reseller</th>
              <th>Exportar</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-4">
                  Cargando...
                </td>
              </tr>
            ) : lots.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-4">
                  No hay lotes disponibles
                </td>
              </tr>
            ) : (
              lots.map((lot, index) => {
                const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;
                const isSelected = selectedRow === index;
                const lotKey = lot.identify || lot.lot_number || lot.id || `${currentPage}-${index}`;
                const resellerInfo = lotResellerMap[lotKey];
                const exportLotId = lot.id || lot.identify || lot.lot_number;
                
                return (
                  <tr
                    key={lotKey}
                    className={isSelected ? 'selected-row' : ''}
                    onClick={() => {
                      const lotIdentify = lot.identify || lot.lot_number || lot.id;
                      setSelectedRow(isSelected ? null : index);
                      if (lotIdentify) {
                        navigate(`/pins/qa/estado?type=lot&identify=${encodeURIComponent(lotIdentify)}`);
                      }
                    }}
                  >
                    <td>{rowNumber}</td>
                    <td>{lot.identify || lot.lot_number || 'N/A'}</td>
                    <td>
                      {lot.observations && Array.isArray(lot.observations) && lot.observations.length > 0
                        ? lot.observations[0].message
                        : lot.message || lot.observation || lot.note || 'N/A'}
                    </td>
                    <td>{formatDate(lot.created_at || lot.created || lot.date_creation)}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span>
                          {resellerInfo?.channelName || 'Sin reseller'}
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            openResellerModal(lotKey);
                          }}
                        >
                          {resellerInfo ? 'Cambiar' : 'Asignar'}
                        </button>
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleExportLot(exportLotId);
                        }}
                      >
                        CSV
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {(hasPrevPage || hasNextPage) && (
        <div className="pins-lotes-pagination">
          <button
            className="pagination-button"
            onClick={() => { setSelectedRow(null); fetchLots(prevPageParam); }}
            disabled={!hasPrevPage}
          >
            ← Anterior
          </button>
          <span className="pagination-info">Página {currentPage}</span>
          <button
            className="pagination-button"
            onClick={() => { setSelectedRow(null); fetchLots(nextPageParam); }}
            disabled={!hasNextPage}
          >
            Siguiente →
          </button>
        </div>
      )}

      {!isReadOnly && (
        <button
          type="button"
          className="pins-lotes-fab"
          onClick={() => setShowCreateModal(true)}
          aria-label="Agregar lote"
        >
          +
        </button>
      )}

      {showCreateModal && !isReadOnly && (
        <div className="pins-lotes-modal-backdrop" onClick={() => setShowCreateModal(false)}>
          <div
            className="pins-lotes-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="pins-lotes-modal-close"
              onClick={() => setShowCreateModal(false)}
              aria-label="Cerrar"
            >
              ×
            </button>
            <h2 className="pins-lotes-modal-title">Generar Lote</h2>
            <div className="pins-lotes-modal-field">
              <label>Cantidad de PINes</label>
              <input
                type="number"
                min="1"
                value={pinQuantity}
                onChange={(event) => setPinQuantity(event.target.value)}
                placeholder="Ej: 100"
              />
            </div>
            <div className="pins-lotes-modal-field">
              <label>Observación (obligatorio)</label>
              <textarea
                rows="3"
                value={lotNote}
                onChange={(event) => setLotNote(event.target.value)}
                placeholder="Notas del lote"
                required
              />
              {!lotNote.trim() && (
                <div className="text-danger mt-1">
                  La observación es obligatoria para generar el lote.
                </div>
              )}
            </div>
            <button
              type="button"
              className="pins-lotes-modal-action"
              onClick={handleCreateLot}
              disabled={creatingLot || !pinQuantity || !lotNote.trim()}
            >
              {creatingLot ? 'Generando...' : 'Generar Lote'}
            </button>
          </div>
        </div>
      )}

      {resellerModalOpen && (
        <div className="pins-lotes-modal-backdrop" onClick={() => setResellerModalOpen(false)}>
          <div
            className="pins-lotes-modal pins-lotes-modal--reseller"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="pins-lotes-modal-close"
              onClick={() => setResellerModalOpen(false)}
              aria-label="Cerrar"
            >
              ×
            </button>
            <h2 className="pins-lotes-modal-title">Seleccionar Reseller</h2>
            <div className="pins-lotes-modal-body">
              <Channels_link token={sessionToken} onSet={handleSetReseller} />
            </div>
          </div>
        </div>
      )}

      {exportInProgress && (
        <div className="pins-lotes-export-backdrop">
          <div className="pins-lotes-export-modal">
            <div className="pins-lotes-export-icon">!</div>
            <h3>¡Espera un momento!</h3>
            <p>Estamos generando tu archivo.</p>
          </div>
        </div>
      )}

      {toastMessage && (
        <div className={`pins-lotes-toast pins-lotes-toast--${toastVariant}`}>
          <span>{toastMessage}</span>
          <button
            type="button"
            className="pins-lotes-toast-close"
            onClick={() => setToastMessage('')}
            aria-label="Cerrar notificación"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

export default PinsLotesQA;
